import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Paper,
  Link,
  Button,
  Alert,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import BaseResourceViewer from '../BaseResourceViewer';
import authService from '../../../../services/authService';
import { downloadFile as apiDownloadFile } from '../../../../services/fileApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const fileContentCache = new Map();

export const clearFileCacheEntry = (fileId) => {
  if (fileContentCache.has(fileId)) {
    console.log(`[FileCache] Clearing cache entry for ${fileId}`);
    fileContentCache.delete(fileId);
    return true;
  }
  console.log(`[FileCache] Attempted to clear non-existent cache entry for ${fileId}`);
  return false;
};

const guessMimeType = (fileType, contentType, filename) => {
    const genericType = 'application/octet-stream';
    const ft = fileType?.toLowerCase();
    const ext = filename?.split('.').pop()?.toLowerCase();

    if (contentType && contentType !== genericType) return contentType;

    if (ft === 'png') return 'image/png';
    if (ft === 'jpg' || ft === 'jpeg') return 'image/jpeg';
    if (ft === 'gif') return 'image/gif';
    if (ft === 'webp') return 'image/webp';
    if (ft === 'svg') return 'image/svg+xml';
    if (ft === 'pdf') return 'application/pdf';
    if (ft === 'txt') return 'text/plain';
    if (ft === 'csv') return 'text/csv';
    if (ft === 'html') return 'text/html';
    if (ft === 'css') return 'text/css';
    if (ft === 'javascript') return 'text/javascript';
    if (ft === 'json') return 'application/json';

    if (ext === 'png') return 'image/png';
    if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
    if (ext === 'gif') return 'image/gif';
    if (ext === 'webp') return 'image/webp';
    if (ext === 'svg') return 'image/svg+xml';
    if (ext === 'pdf') return 'application/pdf';
    if (ext === 'txt') return 'text/plain';
    if (ext === 'csv') return 'text/csv';
    if (ext === 'html') return 'text/html';
    if (ext === 'css') return 'text/css';
    if (ext === 'js') return 'text/javascript';
    if (ext === 'json') return 'application/json';

    return contentType || genericType;
};

const FileResourceViewer = ({ resource, onNavigateBack, backButtonText }) => {
  const [objectUrl, setObjectUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const objectUrlRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const effectiveContentType = guessMimeType(
      resource?.file_type,
      resource?.content_type,
      resource?.original_filename
  );

  const isImage = effectiveContentType.startsWith('image/');
  const isPdf = effectiveContentType === 'application/pdf';
  const isText = effectiveContentType.startsWith('text/') || effectiveContentType === 'application/json';
  const canPreview = isImage || isPdf || isText;

  useEffect(() => {
    if (objectUrlRef.current) {
      console.log("[FileViewer] Cleanup: Revoking previous object URL:", objectUrlRef.current.substring(0, 50));
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setObjectUrl(null);
    setTextContent(null);
    setIsLoading(false);
    setFetchError(null);

    console.log("[FileViewer] Effect running for resource:", resource?.id);
    if (!resource || !resource.id) {
      console.error("[FileViewer] Invalid resource prop:", resource);
      setFetchError("Invalid file resource data provided.");
      return;
    }

    const { id: fileId } = resource;

    if (fileContentCache.has(fileId)) {
      const cachedData = fileContentCache.get(fileId);
      console.log(`[FileViewer] Cache HIT for ${fileId}. Type: ${cachedData.type}`);
      if (cachedData.type === 'blob' && (isImage || isPdf)) {
        const newUrl = URL.createObjectURL(cachedData.data);
        objectUrlRef.current = newUrl;
        setObjectUrl(newUrl);
        console.log("[FileViewer] Created new object URL from cached blob:", newUrl.substring(0, 50));
      } else if (cachedData.type === 'text' && isText) {
        setTextContent(cachedData.data);
      } else {
        console.warn(`[FileViewer] Cache data type mismatch or cannot preview for ${fileId}. Type: ${cachedData.type}, CanPreview: ${canPreview}`);
        if (!canPreview) {
             return;
        }
      }
      if ((cachedData.type === 'blob' && (isImage || isPdf)) || (cachedData.type === 'text' && isText)) {
          return;
      }
    }

    if (!canPreview) {
      console.log(`[FileViewer] Preview not supported for effective type: ${effectiveContentType}`);
      return;
    }

    const backendDownloadUrl = `${API_BASE_URL}/api/v1/files/${fileId}/download`;
    console.log(`[FileViewer] Cache MISS for ${fileId}. Fetching: ${backendDownloadUrl}`);

    const fetchFileContent = async () => {
      setIsLoading(true);
      setFetchError(null);
      let currentObjectUrl = null;
      try {
        const response = await authService.authFetch(backendDownloadUrl);
        console.log(`[FileViewer] Fetch response status: ${response.status}`);
        if (!response.ok) {
          let errorDetail = `Fetch failed: ${response.status} ${response.statusText}`;
           try {
               const errorData = await response.json();
               errorDetail = errorData.detail || errorDetail;
               console.error("[FileViewer] Fetch error detail from JSON:", errorData);
           } catch(e) { console.warn("[FileViewer] Fetch error response was not JSON."); }
          throw new Error(errorDetail);
        }

        if (isText) {
          const text = await response.text();
          console.log("[FileViewer] Fetched text content.");
          setTextContent(text);
          fileContentCache.set(fileId, { type: 'text', data: text });
          console.log(`[FileViewer] Stored text content in cache for ${fileId}`);
        } else if (isImage || isPdf) {
          const blob = await response.blob();
          console.log(`[FileViewer] Fetched blob content. Size: ${blob.size}, Type: ${blob.type}`);
          const blobWithType = blob.type === 'application/octet-stream' ? blob.slice(0, blob.size, effectiveContentType) : blob;
          fileContentCache.set(fileId, { type: 'blob', data: blobWithType });
          console.log(`[FileViewer] Stored blob in cache for ${fileId}`);
          currentObjectUrl = URL.createObjectURL(blobWithType);
          objectUrlRef.current = currentObjectUrl;
          setObjectUrl(currentObjectUrl);
          console.log("[FileViewer] Created object URL from fetched blob:", currentObjectUrl.substring(0, 50));
        }
      } catch (error) {
        console.error("[FileViewer] Error during file content fetch:", error);
        setFetchError(`Failed to load file preview: ${error.message}`);
        setTextContent(null);
        setObjectUrl(null);
        if (currentObjectUrl) {
            URL.revokeObjectURL(currentObjectUrl);
            objectUrlRef.current = null;
        }
      } finally {
        setIsLoading(false);
        console.log("[FileViewer] Fetch process finished.");
      }
    };

    fetchFileContent();

    return () => {
      if (objectUrlRef.current) {
        console.log("[FileViewer] Effect cleanup: Revoking instance object URL:", objectUrlRef.current.substring(0, 50));
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [resource?.id, effectiveContentType, canPreview]);

  const handleDownload = async () => {
    if (!resource || !resource.id) {
      setDownloadError("File ID is missing.");
      return;
    }
    setIsDownloading(true);
    setDownloadError(null);
    try {
      const { blob, filename: actualFilename } = await apiDownloadFile(resource.id, resource.original_filename);
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = actualFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Error downloading file:", err);
      setDownloadError(err.message || "Failed to download file.");
    } finally {
      setIsDownloading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === undefined || bytes === null || bytes < 0) return 'N/A';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (!resource) {
    return (
      <BaseResourceViewer resource={{}} title="File Viewer">
        <Alert severity="warning">No file resource data provided.</Alert>
      </BaseResourceViewer>
    );
  }

  const {
    original_filename = 'Unknown File',
    file_type,
    id: fileId
  } = resource;

  const baseViewerTitle = `File: ${original_filename}`;

  const renderContent = () => {
    if (isLoading) {
       return (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Skeleton variant="rectangular" height={isText ? 200 : 300} width="90%" sx={{ mb: 1 }}/>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CircularProgress size={20} sx={{ mr: 1 }}/>
                <Typography variant="body2">Loading preview...</Typography>
            </Box>
          </Box>
       );
    }
    if (fetchError) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {fetchError}
        </Alert>
      );
    }
    if (isImage && objectUrl) {
      return (
        <Box sx={{ textAlign: 'center', my: 2 }}>
          <img src={objectUrl} alt={original_filename} style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }}/>
        </Box>
      );
    }
    if (isPdf && objectUrl) {
      return (
        <Box sx={{ height: '70vh', my: 2, border: '1px solid', borderColor: 'divider' }}>
          <iframe src={objectUrl} title={original_filename} width="100%" height="100%" style={{ border: 'none' }}/>
        </Box>
      );
    }
    if (isText && textContent !== null) {
      return (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, maxHeight: '60vh', overflow: 'auto', bgcolor: 'background.paper' }}>
          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '0.875rem' }}>
            {textContent}
          </Typography>
        </Paper>
      );
    }
    if (!canPreview) {
        return (
          <Alert severity="info" sx={{ mt: 2 }}>
            Direct preview is not available for this file type ({file_type || effectiveContentType}). Please use the download button.
          </Alert>
        );
    }
    return <Alert severity="warning" sx={{ mt: 2 }}>Could not display preview.</Alert>;
  };

  return ( 
    <BaseResourceViewer resource={resource} title={baseViewerTitle} onNavigateBack={onNavigateBack} backButtonText={backButtonText}>
      {renderContent()}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={isDownloading || !resource?.id}
          startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
        >
          {isDownloading ? 'Downloading...' : 'Download File'}
        </Button>
      </Box>
      {downloadError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {downloadError}
        </Alert>
      )}
    </BaseResourceViewer>
  );
};

FileResourceViewer.propTypes = {
  resource: PropTypes.shape({
    id: PropTypes.string.isRequired,
    original_filename: PropTypes.string,
    content_type: PropTypes.string,
    file_size: PropTypes.number,
    file_type: PropTypes.string,
    type: PropTypes.string,
    resource_type: PropTypes.string
  }).isRequired,
  onNavigateBack: PropTypes.func,
  backButtonText: PropTypes.string,
};

export default FileResourceViewer;